const STORAGE_PREFIX = 'fonexpress:scroll:';

/** In-memory + sessionStorage so back navigation survives remounts */
const scrollPositions = new Map();

/** Routes that were reached via browser back — finish restore after async layout */
const popRestoreKeys = new Set();

export function routeKey(pathname, search = '') {
    return `${pathname}${search}`;
}

export function saveScrollPosition(key, y) {
    if (y == null || y < 0) return;
    const rounded = Math.round(y);
    const prev = scrollPositions.get(key);
    if (prev != null && prev === rounded) return;
    scrollPositions.set(key, rounded);
    try {
        sessionStorage.setItem(STORAGE_PREFIX + key, String(rounded));
    } catch {
        /* ignore quota / private mode */
    }
}

export function getScrollPosition(key) {
    if (scrollPositions.has(key)) {
        return scrollPositions.get(key);
    }
    try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
        if (raw != null) {
            const y = parseInt(raw, 10);
            if (!Number.isNaN(y)) {
                scrollPositions.set(key, y);
                return y;
            }
        }
    } catch {
        /* ignore */
    }
    return null;
}

function applyScrollInstant(y) {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.scrollBehavior;
    const prevBody = body.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    html.style.scrollBehavior = prevHtml;
    body.style.scrollBehavior = prevBody;
}

function maxScrollY() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
}

function isRestoreComplete(target) {
    const maxScroll = maxScrollY();
    const y = Math.min(target, maxScroll);
    const atTarget = Math.abs(window.scrollY - y) <= 5;
    const pageTallEnough = maxScroll >= target - 80;
    return atTarget && pageTallEnough;
}

export function markPopRestore(key) {
    popRestoreKeys.add(key);
}

export function shouldPopRestore(key) {
    return popRestoreKeys.has(key);
}

export function clearPopRestore(key) {
    popRestoreKeys.delete(key);
}

export function markPendingScrollRestore(key) {
    popRestoreKeys.add(key);
}

export function flushPendingScrollRestore(key) {
    if (!popRestoreKeys.has(key)) return () => {};
    return restoreScrollPosition(key, { aggressive: true, untilReady: true });
}

/**
 * Retry restore until page height can accommodate target Y (images/CMS sections).
 */
export function restoreScrollPosition(key, { aggressive = false, untilReady = false } = {}) {
    const target = getScrollPosition(key);
    if (target == null || target <= 0) {
        return () => {};
    }

    let cancelled = false;
    let resizeObserver = null;
    let layoutTimeout = null;
    const maxAttempts = aggressive ? 40 : 15;
    const interval = aggressive ? 100 : 100;

    const tryApply = () => {
        if (cancelled) return false;
        const y = Math.min(target, maxScrollY());
        applyScrollInstant(y);
        return isRestoreComplete(target);
    };

    const finish = (success = false) => {
        if (success) clearPopRestore(key);
        resizeObserver?.disconnect();
        resizeObserver = null;
        if (layoutTimeout) {
            clearTimeout(layoutTimeout);
            layoutTimeout = null;
        }
    };

    const cancel = () => {
        cancelled = true;
        finish(false);
    };

    const watchLayout = () => {
        if (resizeObserver || cancelled) return;

        resizeObserver = new ResizeObserver(() => {
            if (tryApply()) {
                cancelled = true;
                finish(true);
            }
        });
        resizeObserver.observe(document.documentElement);
        if (document.body) resizeObserver.observe(document.body);

        document.querySelectorAll('img').forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', () => tryApply(), { once: true });
            }
        });

        layoutTimeout = setTimeout(() => {
            if (!cancelled) {
                const ok = tryApply();
                cancelled = true;
                finish(ok);
            }
        }, aggressive ? 12000 : 6000);
    };

    const tryRestore = (attempt = 0) => {
        if (cancelled) return;

        if (tryApply()) {
            cancelled = true;
            finish(true);
            return;
        }

        if (attempt + 1 < maxAttempts) {
            setTimeout(() => tryRestore(attempt + 1), interval);
            return;
        }

        if (untilReady) {
            watchLayout();
            return;
        }

        cancelled = true;
        finish(false);
    };

    tryRestore(0);
    requestAnimationFrame(() => tryRestore(0));

    return cancel;
}

/** Call once at app startup — saves scroll before React Router navigates away */
export function initScrollPositionCapture(getRouteKey) {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
    }

    const saveCurrent = () => {
        saveScrollPosition(getRouteKey(), window.scrollY);
    };

    window.addEventListener(
        'scroll',
        () => {
            saveScrollPosition(getRouteKey(), window.scrollY);
        },
        { passive: true }
    );

    const saveBeforeNav = (e) => {
        const anchor = e.target.closest('a[href]');
        if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:'))
            return;
        if (/^https?:\/\//i.test(href)) return;
        saveCurrent();
    };

    document.addEventListener('pointerdown', saveBeforeNav, true);
    document.addEventListener('click', saveBeforeNav, true);

    window.addEventListener('pagehide', saveCurrent);
}
